import styled from 'styled-components';
import { ParagraphSmallText } from '../Texts';
import Button from '../Button';
import { useDispatch, useSelector } from 'react-redux';
import { openModal } from '../../redux/slices/modals';
import {
  useGetCurrentUserProfileQuery,
  useGetCurrentUserRewardsQuery,
} from '../../redux/api/authorized/auth';
import { useGetPublicRewardsQuery } from '../../redux/api/public';
import { usePrivy } from '@privy-io/react-auth';
import { selectPrivyToken } from '../../redux/slices/auth';
import { useNavigate } from 'react-router-dom';
import useCustomLogin from '../../hooks/useCustomLogin';
import { selectTokensWithUSDBalance } from '../../redux/slices/tokenSlice';
import { PHONE_MEDIA_QUERY, useMediaQuery } from '../../common/responsive';

const Container = styled.div`
  max-width: 100%;
  width: 100%;
  overflow-x: auto;
  padding-top: 20px;
`;

const InnerContainer = styled.div`
  display: flex;
  gap: 16px;
  min-width: max-content;
`;

const Item = styled.div`
  width: 264px;
  height: 167px;
  border: 1px solid ${({ theme }) => theme.border.dashed};
  border-radius: 16px;
  padding: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
  text-align: center;
`;

const Image = styled.img`
  width: 186px;
  margin-top: -30px;
  ${({ disabled }) => disabled && 'filter: grayscale(100%);'}
`;

const Rewards = () => {
  const dispatch = useDispatch();

  const navigate = useNavigate();

  const privyToken = useSelector(selectPrivyToken);
  const { login } = useCustomLogin();
  const { user } = usePrivy();
  const { data: rewardsUserData } = useGetCurrentUserRewardsQuery();
  const { data: rewardsPublicData } = useGetPublicRewardsQuery();
  const tokens = useSelector((state) => selectTokensWithUSDBalance(state));
  const { data: userData } = useGetCurrentUserProfileQuery(undefined, {
    skip: !privyToken,
  });

  const data = user ? rewardsUserData : rewardsPublicData;

  const isMobile = useMediaQuery(PHONE_MEDIA_QUERY);

  // Sort function to prioritize certain reward types
  const sortRewards = (rewards) => {
    if (!rewards) return [];

    const priorityOrder = ['FIRST_DEPOSIT', 'TRADE', 'FIRST_TWO_GIFTS'];

    return [...rewards].sort((a, b) => {
      const aIndex = priorityOrder.indexOf(a.type);
      const bIndex = priorityOrder.indexOf(b.type);

      // If both are priority items, maintain their defined order
      if (aIndex !== -1 && bIndex !== -1) {
        return aIndex - bIndex;
      }

      // If only a is priority, it comes first
      if (aIndex !== -1) {
        return -1;
      }

      // If only b is priority, it comes first
      if (bIndex !== -1) {
        return 1;
      }

      // If neither is priority, sort alphabetically by type
      return a.type.localeCompare(b.type);
    });
  };

  const componentMapping = {
    FIRST_DEPOSIT: (status) => {
      let disabled = status === 'COMPLETED';
      return (
        <Item>
          <Image disabled={disabled} src='/rewards/deposit.webp' />
          <ParagraphSmallText style={{ fontWeight: 600 }}>
            Boost your portfolio. Get a $2 bonus with your first deposit.
          </ParagraphSmallText>
          <div style={{ marginTop: 'auto', width: '100%' }}>
            <Button
              disabled={disabled}
              onClick={() => {
                if (!disabled) {
                  if (!user) {
                    login();
                  } else {
                    dispatch(openModal({ name: 'addFunds' }));
                  }
                }
              }}
              fullWidth
              variant={'secondary'}
              style={{ height: 32 }}
            >
              Deposit Now
            </Button>
          </div>
        </Item>
      );
    },
    FIRST_TWO_GIFTS: (status) => {
      let disabled = status === 'COMPLETED';
      return (
        <Item>
          <Image disabled={disabled} src='/rewards/gifts-element.webp' />
          <ParagraphSmallText style={{ fontWeight: 600 }}>
            Pay it forward - send crypto to a friend and get a $2 bonus.
          </ParagraphSmallText>
          <div style={{ marginTop: 'auto', width: '100%' }}>
            <Button
              disabled={disabled}
              onClick={() => {
                if (!disabled) {
                  if (!user) {
                    login();
                  } else {
                    dispatch(openModal({ name: 'selectToken' }));
                  }
                }
              }}
              fullWidth
              variant={'secondary'}
              style={{ height: 32 }}
            >
              Send a Gift
            </Button>
          </div>
        </Item>
      );
    },
    REFERRAL: (status) => {
      let disabled = status === 'COMPLETED';
      return (
        <Item>
          <Image disabled={disabled} src='/rewards/referrals-element.webp' />
          <ParagraphSmallText style={{ fontWeight: 600 }}>
            Refer friends and earn crypto together.
          </ParagraphSmallText>
          <div style={{ marginTop: 'auto', width: '100%' }}>
            <Button
              disabled={disabled}
              onClick={() => {
                if (!disabled) {
                  if (!user) {
                    login();
                  } else {
                    dispatch(openModal({ name: 'referrals' }));
                  }
                }
              }}
              fullWidth
              variant={'secondary'}
              style={{ height: 32 }}
            >
              Invite Friends
            </Button>
          </div>
        </Item>
      );
    },
    SHARE: (status) => {
      let disabled = status === 'COMPLETED';
      return (
        <Item>
          <Image disabled={disabled} src='/rewards/share-element.webp' />
          <ParagraphSmallText style={{ fontWeight: 600 }}>
            Share Your Gift Experience on social media and get a $1 reward.
          </ParagraphSmallText>
          <div style={{ marginTop: 'auto', width: '100%' }}>
            <Button fullWidth variant={'secondary'} style={{ height: 32 }}>
              Share Now
            </Button>
          </div>
        </Item>
      );
    },
    SHARE_X: (status) => {
      let disabled = status === 'COMPLETED';
      return (
        <Item>
          <Image disabled={disabled} src='/rewards/share-element.webp' />
          <ParagraphSmallText style={{ fontWeight: 600 }}>
            Share on X to enter our $50 weekly draw.
          </ParagraphSmallText>
          <div style={{ marginTop: 'auto', width: '100%' }}>
            <Button
              disabled={disabled}
              onClick={() => {
                if (!disabled) {
                  if (!user) {
                    login();
                  } else {
                    window.open(
                      `https://twitter.com/intent/tweet?text=I’m using %40deksxyz to send crypto gifts 🎁%0ACashback on first deposit %2B first two gifts. Entering the %2450 weekly draw—start here%3A https%3A%2F%2Fdeks.xyz%2F%3Frc%3D${userData?.referralCode}%0A%23deksWeeklyDraw`,
                    );
                  }
                }
              }}
              fullWidth
              variant={'secondary'}
              style={{ height: 32 }}
            >
              Share Now
            </Button>
          </div>
        </Item>
      );
    },
    TRADE: (status) => {
      let disabled = status === 'COMPLETED';
      return (
        <Item>
          <Image disabled={disabled} src='/rewards/trade-element.webp' />
          <ParagraphSmallText style={{ fontWeight: 600 }}>
            Make your first trade. Explore other assets - your first trade is on
            us.
          </ParagraphSmallText>
          <div style={{ marginTop: 'auto', width: '100%' }}>
            <Button
              fullWidth
              variant={'secondary'}
              style={{ height: 32 }}
              onClick={() => {
                if (isMobile) {
                  navigate(`/app/trade`);
                } else {
                  dispatch(
                    openModal({
                      name: 'selectToken',
                      props: {
                        title: 'Select an Asset to Trade',
                        onContinue: (token) => {
                          navigate(`/app/tokens/${token.address}?tab=trade`);
                        },
                      },
                    }),
                  );
                }
              }}
            >
              Trade Now
            </Button>
          </div>
        </Item>
      );
    },
  };

  return (
    <Container id='HowToEarnMore'>
      <InnerContainer>
        {sortRewards(data)?.map((reward) => {
          return componentMapping?.[reward.type]?.(reward.status);
        })}
      </InnerContainer>
    </Container>
  );
};

export default Rewards;
